import { Actions } from 'vuex-smart-module';
import { ResponseStatuses } from '@/enums';
import { Filters, FiltersPagination } from '@/types';
import { CommercialOffersService } from '../services';
import CommercialOffersState from './state';
import CommercialOffersMutations from './mutations';
import CommercialOffersGetters from './getters';
import { CommercialOffers } from '../types';

const commercialOffersService = new CommercialOffersService();

export default class CommercialOffersActions extends Actions<
  CommercialOffersState,
  CommercialOffersGetters,
  CommercialOffersMutations,
  // eslint-disable-next-line no-use-before-define
  CommercialOffersActions
> {
  // ------------------------------------------ Offer templates ------------------------------------------

  async get_offers_templates(storage_limit?: number): Promise<void> {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.filters,
        pagination: {
          limit: storage_limit ?? itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_LOADING_OFFER_TEMPLATES(true);
      const { data, meta } = await commercialOffersService.get_offers_templates(payload);

      this.mutations.SET_OFFERS_TEMPLATES(data.items);
      this.mutations.SET_ORGANIZATIONS_FOR_FILTERS(data.organizations);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_LOADING_OFFER_TEMPLATES(false);
    }
  }

  async get_offer_template_by_id(id: string | number) {
    const { data, code } = await commercialOffersService.get_offer_template_by_id(id);

    if (code === ResponseStatuses.Success.OK) {
      this.mutations.SET_CURRENT_OFFER_TEMPLATE(data);
    }

    return code;
  }

  async add_offer_template() {
    const { new_offer_template } = this.state;
    const form_data = new FormData();

    Object.keys(new_offer_template).forEach((key) => {
      const value = (new_offer_template as Record<string, unknown>)[key];

      if (key !== 'organizations' && key !== 'files') {
        if (typeof value === 'string') {
          form_data.append(key, value);
        }
      }
    });

    if (new_offer_template.organizations) {
      new_offer_template.organizations.forEach((org, index) => {
        form_data.append(`organizations[${index}][id]`, org.id.toString());
      });
    }

    if (new_offer_template.files) {
      new_offer_template.files.forEach((file, index) => {
        if (file.file) {
          form_data.append(`files[${index}][file]`, file.file);
        }
        form_data.append(`files[${index}][title]`, file.title);
        form_data.append(`files[${index}][is_active]`, JSON.stringify(file.is_active));
      });
    }

    try {
      const data = await commercialOffersService.add_offer_template(form_data);

      if (data.code === ResponseStatuses.Success.CREATED) {
        this.dispatch('get_offers_templates');
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  }

  async update_offer_template(id: string | number) {
    const { new_offer_template } = this.state;
    const form_data = new FormData();

    Object.keys(new_offer_template).forEach((key) => {
      const value = (new_offer_template as Record<string, unknown>)[key];

      if (key !== 'organizations' && key !== 'files') {
        if (typeof value === 'string') {
          form_data.append(key, value);
        }
      }
    });

    if (new_offer_template.organizations) {
      new_offer_template.organizations.forEach((org, index) => {
        form_data.append(`organizations[${index}][id]`, org.id.toString());
      });
    }

    if (new_offer_template.files) {
      new_offer_template.files.forEach((file, index) => {
        if (file.file) {
          form_data.append(`files[${index}][file]`, file.file);
        }
        form_data.append(`files[${index}][title]`, file.title);
        form_data.append(`files[${index}][is_active]`, JSON.stringify(file.is_active));
      });
    }

    try {
      const { data, code } = await commercialOffersService.update_offer_template(id, form_data);

      return { data, code };
    } catch (error) {
      console.error(error);
    }
  }

  async delete_offer_template(id: string | number) {
    try {
      const data = await commercialOffersService.delete_offer_template(id);

      if (data.code === ResponseStatuses.Success.UPDATED) {
        this.dispatch('get_offers_templates');
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  }

  async delete_files(payload: {id: string | number, files: number[]}) {
    const { id, files } = payload;

    try {
      const data = await commercialOffersService.delete_files(id, files);

      if (data.code === ResponseStatuses.Success.UPDATED) {
        this.dispatch('get_offer_template_by_id', id);
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  }

  async active_file(file_id: number) {
    const data = await commercialOffersService.active_file(file_id);

    if (data.code === ResponseStatuses.Success.UPDATED) {
      this.mutations.UPD_ACTIVE_FILE(file_id);
    }
  }

  // ------------------------------------------ Commercial Offers ------------------------------------------

  async add_new_offer() {
    try {
      const data = await commercialOffersService.add_new_offer(this.getters.commercialProposalRequest);

      if (data.data.commercial_proposal_id && this.getters.hasCommercialProposalFiles) {
        await this.actions.update_commercial_offer_files(data.data.commercial_proposal_id);
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  }

  async update_commercial_offer(id: string | number) {
    try {
      const data = await commercialOffersService.update_commercial_offer(id, this.getters.commercialProposalRequest);

      if (this.getters.hasCommercialProposalFiles) {
        await this.actions.update_commercial_offer_files(id);
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  }

  async update_commercial_offer_files(id: string | number) {
    try {
      return await commercialOffersService
        .update_commercial_offer_files(id, this.getters.commercialProposalFileUpdateRequest);
    } catch (error) {
      console.error(error);
    }
  }

  async get_commercial_offers() {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options_offers_list;

      const payload: FiltersPagination<Filters> = {
        filters: this.state.offers_filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_LOADING_OFFERS(true);
      const { data, meta } = await commercialOffersService.get_commercial_offers(payload);

      this.mutations.SET_COMMERCIAL_OFFERS(data.items);
      this.mutations.SET_CLIENTS_FOR_FILTERS(data.clients);
      this.mutations.SET_HAS_TEMPLATE(data.has_template);
      this.mutations.UPD_OFFERS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_OFFERS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_PAGINATION_OFFERS_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_OFFERS_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_OFFERS_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_OFFERS_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_LOADING_OFFERS(false);
    }
  }

  async get_commercial_offer_by_id(id: string | number) {
    try {
      const data = await commercialOffersService.get_commercial_offer_by_id(id);

      if (data.data.id) {
        this.mutations.SET_CURRENT_OFFER(data.data);
        this.mutations.SET_EDITING_OFFER();
      }

      return data;
    } catch (error) {
      console.error(error);
    }
  }

  async delete_commercial_offer(id: string | number) {
    try {
      const { code } = await commercialOffersService.delete_commercial_offer(id);

      if (code === ResponseStatuses.Success.UPDATED) {
        this.dispatch('get_commercial_offers');
      }

      return code;
    } catch (error) {
      console.error(error);
    }
  }

  // ------------------------------------------ Catalog products ------------------------------------------

  async get_catalog_products(filtersEmpty: boolean) {
    this.mutations.SET_CATALOG_PRODUCTS_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.catalog_products_table_options;

      const payload: CommercialOffers.CatalogProductRequest = {
        filters: this.state.catalog_products_filters,
        filters_exist: !filtersEmpty,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await commercialOffersService.get_catalog_products(payload);

      this.mutations.SET_CATALOG_PRODUCTS(data.items);

      this.mutations.UPD_CATALOG_PRODUCTS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_CATALOG_PRODUCTS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'limit', value: meta.limit });

      return data.filters;
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_CATALOG_PRODUCTS_LOADING(false);
    }
  }
}
